import clsx from "clsx"
import { forwardRef, memo } from "react"

import { faIcon } from "@utils/faIcon"

import { HotKey, Icon, NavigationLink } from "@atoms"

import { HotKeyStack } from "@molecules"

import styles from "./NavSidebarItem.module.scss"
import { NavSidebarItemProps } from "./NavSidebarItem.types"

function NavSidebarItem(props: NavSidebarItemProps, ref: React.Ref<HTMLAnchorElement>) {
    const {
        variant = "default",
        className,

        label,
        icon,
        iconActive,

        modifier,
        showNotification,
        symbolName,
        symbolNames,

        path,
        onClick,
        onActive,

        isEmphasized = false,
        isExpanded = false,
        isDisabled = false,
        isActive = undefined,
        _isFocused = false,
        _isHovered = false,
        isPressed = false,
    } = props

    const isKeyboardNavigationEnabled = !isDisabled && (variant !== "groupItem" || isExpanded)

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        isDisabled ? event.preventDefault() : onClick?.(event)
    }

    const renderIcon = (isActive: boolean) => {
        if (!icon || variant === "groupItem") {
            return null
        } else {
            return (
                <Icon
                    icon={icon}
                    iconOnActive={iconActive}
                    isActive={isActive || isExpanded}
                    className={styles.icon}
                    size={14}
                />
            )
        }
    }

    const renderModifier = () => {
        if (modifier === "notification" && showNotification) {
            return (
                <Icon
                    icon={faIcon({ name: "circle-small", style: "solid" })}
                    size={8}
                    className={styles.notification}
                />
            )
        }

        if (modifier === "hotkey" && symbolName) {
            return <HotKey symbolName={symbolName} className={styles.hotkey} />
        }

        if (modifier === "hotkeyStack" && symbolNames) {
            return (
                <HotKeyStack>
                    {symbolNames.map((name) => (
                        <HotKey key={name} symbolName={name} className={styles.hotkey} />
                    ))}
                </HotKeyStack>
            )
        }

        return null
    }

    return (
        <NavigationLink
            ref={ref}
            title={label}
            path={path}
            className={clsx(styles.base, className, styles[variant], {
                [styles.emphasized]: isEmphasized,
                [styles.disabled]: isDisabled,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
                [styles.active]: isActive,
                [styles.expanded]: isExpanded,
            })}
            activeClassName={styles.active}
            onClick={handleClick}
            isTabbable={isKeyboardNavigationEnabled}
            isActive={isActive}
            onActive={onActive}
        >
            {(isActive) => (
                <>
                    {renderIcon(isActive)}
                    <span className={styles.text}>{label}</span>

                    {variant === "groupHead" && (
                        <div className={styles.chevronWrap}>
                            <Icon
                                size={12}
                                icon={faIcon({ name: "caret-left", style: "solid" })}
                                className={clsx(styles.chevron, { [styles.expanded]: isExpanded })}
                                container="square"
                            />
                        </div>
                    )}

                    {renderModifier()}
                </>
            )}
        </NavigationLink>
    )
}

export default memo(forwardRef(NavSidebarItem))
